'use client';

import { T } from '@/i18n/LocaleProvider';
import { useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PageSection } from '@/components';
import { MONO_FONT } from '@/theme';
import { useCountUp } from '@/hooks/useCountUp';

// Cifras con fuente primaria verificada (oct 2026). Antes: un 73% atribuido a Capgemini que
// no existe, un 60% zero-click atribuido a Bain (es de SparkToro) y un bloque de ROI sin
// relación con AEO. Ver el informe de mejoras de octubre 2026.
const STATS = [
  {
    num: '−25%',
    countTo: 25,
    prefix: '−',
    suffix: '%',
    neg: true,
    desc: (
      <>
        <T>Caída que Gartner proyectó en 2024 para el volumen de los</T>{' '}
        <strong>
          <T>buscadores tradicionales</T>
        </strong>
        <T> hacia 2026, por los chatbots y agentes de IA.</T>
      </>
    ),
    cite: 'FUENTE · GARTNER · 2024',
  },
  {
    num: '43%',
    countTo: 43,
    suffix: '%',
    neg: false,
    desc: (
      <>
        <T>De las búsquedas en Google ya muestran un</T>{' '}
        <strong>
          <T>AI Overview</T>
        </strong>
        <T>
          , frente al 15% un año antes. La pelea por aparecer en el bloque
          generativo ya no es opcional.
        </T>
      </>
    ),
    cite: 'FUENTE · SIMILARWEB · 2026',
  },
  {
    num: '8%',
    countTo: 8,
    suffix: '%',
    neg: false,
    desc: (
      <>
        <T>
          De las visitas terminan en clic a un resultado cuando Google muestra
          un
        </T>{' '}
        <strong>
          <T>resumen de IA</T>
        </strong>
        <T> (15% sin él). La forma de existir es ser</T>{' '}
        <em>
          <T>citado</T>
        </em>
        <T> en esa respuesta.</T>
      </>
    ),
    cite: 'FUENTE · PEW RESEARCH CENTER · 2025',
  },
];

const ROI = [
  {
    label: 'TRÁFICO DESDE IA',
    value: '+393%',
    countTo: 393,
    prefix: '+',
    suffix: '%',
    sub: (
      <>
        <T>Visitas desde IA al retail de EE. UU., 1T 2026 · </T>
        <strong>
          <T>Adobe</T>
        </strong>
      </>
    ),
  },
  {
    label: 'CONVERSIÓN',
    value: '+42%',
    countTo: 42,
    prefix: '+',
    suffix: '%',
    sub: (
      <>
        <T>Del tráfico de IA frente al resto, marzo 2026 · </T>
        <strong>
          <T>Adobe</T>
        </strong>
      </>
    ),
  },
  {
    label: 'EN EL TOP 10',
    value: '12%',
    countTo: 12,
    suffix: '%',
    sub: (
      <>
        <T>
          De lo que citan ChatGPT, Gemini y Copilot está en el top 10 de Google
          ·
        </T>{' '}
        <strong>
          <T>Ahrefs 2025</T>
        </strong>
      </>
    ),
  },
];

export default function Stats() {
  const sectionRef = useRef(null);
  const statRefs = useCountUp(STATS, sectionRef);
  const roiRefs = useCountUp(ROI, sectionRef);

  return (
    <PageSection
      eyebrow="01 / El Problema"
      title={
        <>
          <T>No dejes que la IA haga tu</T>{' '}
          <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />
          <T></T>{' '}
          <Box
            component="span"
            sx={{
              textDecoration: 'line-through',
              color: 'text.muted',
              fontWeight: 'inherit',
            }}
          >
            <T>marca invisible</T>
          </Box>
          .
        </>
      }
      lede="Con el declive de las búsquedas orgánicas, si tu marca no es la respuesta, no existes. Toda empresa necesita una estrategia de optimización de respuestas basada en IA."
    >
      {/* Stat grid — ref is the ScrollTrigger anchor for all counters */}
      <Box
        ref={sectionRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          borderTop: '1px solid #e2e5e3',
        }}
      >
        {STATS.map((s, i) => (
          <Box
            key={i}
            component="article"
            sx={{
              py: { xs: 4, md: 5 },
              pr: { sm: '36px' },
              pl: { sm: i > 0 ? '36px' : 0 },
              borderRight: { sm: i < 2 ? '1px solid #e2e5e3' : 'none' },
              borderBottom: {
                xs: i < 2 ? '1px solid #e2e5e3' : 'none',
                md: 'none',
              },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              ref={(el) => {
                statRefs.current[i] = el;
              }}
              sx={{
                fontSize: 'clamp(40px, 5vw, 72px)',
                fontWeight: 400,
                letterSpacing: '-0.03em',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
                color: s.neg ? 'text.primary' : 'text.accent',
              }}
            >
              <T>{s.num}</T>
            </Typography>
            <Typography
              sx={{
                fontSize: '15px',
                color: 'grey.600',
                lineHeight: 1.6,
                my: 2,
              }}
            >
              <T>{s.desc}</T>
            </Typography>
            <Typography
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '10.5px',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'grey.600',
                pt: 2,
                borderTop: '1px solid #e2e5e3',
                mt: 'auto',
              }}
            >
              <T>{s.cite}</T>
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ROI strip */}
      <Box
        sx={{
          mt: 5,
          bgcolor: 'primary.dark',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          px: { xs: 3, md: 5 },
          py: 4,
          borderRadius: 2,
          gap: { xs: 3, sm: 0 },
        }}
      >
        {ROI.map((r, i) => (
          <Box
            key={r.label}
            sx={{
              pb: { xs: i < 2 ? 3 : 0, sm: 0 },
              pl: { sm: i > 0 ? '28px' : 0 },
              pr: { sm: '28px' },
              borderRight: {
                sm: i < 2 ? '1px solid rgba(255,255,255,0.18)' : 'none',
              },
              borderBottom: {
                xs: i < 2 ? '1px solid rgba(255,255,255,0.18)' : 'none',
                sm: 'none',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '11px',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#ffffff',
                mb: 1.25,
                fontWeight: 600,
              }}
            >
              <T>{r.label}</T>
            </Typography>
            <Typography
              ref={(el) => {
                roiRefs.current[i] = el;
              }}
              sx={{
                fontSize: 'clamp(28px, 3vw, 38px)',
                lineHeight: 1,
                letterSpacing: '-0.022em',
                fontWeight: 400,
                color: 'common.white',
                mb: 1.25,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <T>{r.value}</T>
            </Typography>
            <Typography
              sx={{
                fontSize: '12.5px',
                lineHeight: 1.4,
                color: 'common.white',
                '& strong': { color: 'common.white', fontWeight: 600 },
              }}
            >
              <T>{r.sub}</T>
            </Typography>
          </Box>
        ))}
      </Box>
    </PageSection>
  );
}
